import { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  Alert,
  Slider,
  FormControlLabel,
  Checkbox,
  Switch,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  OutlinedInput,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PlayArrow,
  Save,
  Download,
  Upload,
  RestaurantMenu,
  LocalDining,
  Kitchen,
  MenuBook,
  PhotoCamera,
  Campaign,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore,
  Star,
  TipsAndUpdates,
  Analytics,
  ContentCopy,
  Delete,
  Edit,
  Warning,
} from '@mui/icons-material';
import {
  CuisinePromptSession,
  CuisinePromptPhase,
  CuisinePrompt,
  FlavorProfile,
  CuisinePromptUseCase,
  StartCuisinePromptRequest,
  CuisinePromptIterateRequest,
  BatchCuisineAnalysisRequest,
} from '../models/cuisinePromptStudio';
import { cuisinePromptStudioService } from '../services/cuisinePromptStudioService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cuisine-tabpanel-${index}`}
      aria-labelledby={`cuisine-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AVAILABLE_CUISINES = [
  // European
  'Italian', 'French', 'Spanish', 'Greek', 'German', 'British', 'Mediterranean',
  // Asian
  'Japanese', 'Chinese', 'Thai', 'Indian', 'Korean', 'Vietnamese', 'Indonesian', 'Filipino',
  // Middle Eastern & North African
  'Middle Eastern', 'Turkish', 'Lebanese', 'Moroccan', 'Persian',
  // American
  'Mexican', 'American', 'Tex-Mex', 'Cajun', 'Soul Food',
  // South American
  'Brazilian', 'Peruvian', 'Argentinian', 'Colombian',
  // African
  'Ethiopian', 'West African', 'South African',
  // Caribbean & Island
  'Caribbean', 'Cuban', 'Puerto Rican', 'Jamaican',
  // Regional/Fusion
  'Fusion', 'Modern American', 'California', 'Pacific Rim',
  // General
  'International', 'General'
];

const USE_CASE_ICONS: Record<CuisinePromptUseCase, React.ReactElement> = {
  'recipe': <RestaurantMenu />,
  'menu': <MenuBook />,
  'blog': <LocalDining />,
  'cookbook': <Kitchen />,
  'food-photography': <PhotoCamera />,
  'marketing': <Campaign />,
};

export const CuisinePromptStudio = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [sessions, setSessions] = useState<CuisinePromptSession[]>([]);
  const [currentSession, setCurrentSession] = useState<CuisinePromptSession | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Backend asset retrieval
  const {
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'CuisinePromptStudio',
    sessionBased: false,
    autoFetch: true,
  });

  // New session form state
  const [concept, setConcept] = useState('');
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [useCase, setUseCase] = useState<CuisinePromptUseCase>('recipe');
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [flavorProfile, setFlavorProfile] = useState<FlavorProfile>({
    sweet: 0.5,
    sour: 0.5,
    salty: 0.5,
    bitter: 0.5,
    umami: 0.5,
    spicy: 0.5,
  });
  const [temperature, setTemperature] = useState(0.7);
  const [includeAuthenticity, setIncludeAuthenticity] = useState(true);
  const [includeModern, setIncludeModern] = useState(false);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const savedSessions = cuisinePromptStudioService.getAllSessions();
    setSessions(savedSessions);
  };

  // Merge backend assets with local data
  useEffect(() => {
    const localData = cuisinePromptStudioService.getAllSessions();

    if (backendAssets && backendAssets.length > 0) {
      const converted: CuisinePromptSession[] = backendAssets.map(asset => {
        const sessionData = asset.textContent ? JSON.parse(asset.textContent) : null;
        return {
          id: asset.id,
          concept: sessionData?.concept || '',
          selectedCuisines: sessionData?.selectedCuisines || [],
          targetUseCase: sessionData?.targetUseCase || 'recipe',
          phases: sessionData?.phases || [],
          generatedPrompts: sessionData?.generatedPrompts || [],
          dietaryRestrictions: sessionData?.dietaryRestrictions || [],
          flavorProfile: sessionData?.flavorProfile || {},
          culturalAuthenticity: sessionData?.culturalAuthenticity ?? false,
          modernInterpretation: sessionData?.modernInterpretation ?? false,
          settings: sessionData?.settings || {},
          createdAt: new Date(asset.createdAt).toISOString(),
          updatedAt: new Date(asset.updatedAt || asset.createdAt).toISOString()
        };
      });

      const backendIds = new Set(converted.map(s => s.id));
      const localOnly = localData.filter(s => !backendIds.has(s.id));
      setSessions([...converted, ...localOnly]);
    }
  }, [backendAssets]);

  const createSession = async () => {
    if (!concept || selectedCuisines.length === 0) {
      setError('Please provide a concept and select at least one cuisine');
      return;
    }

    setIsCreatingSession(true);
    setError(null);

    try {
      const request: StartCuisinePromptRequest = {
        concept,
        cuisines: selectedCuisines,
        useCase,
        dietaryRestrictions,
        flavorProfile,
        settings: {
          temperature,
          includeAuthenticity,
          includeModern,
          emphasizeTechniques: true,
          includeNutritional: false,
        },
      };

      const response = await cuisinePromptStudioService.startSession(request);

      const newSession = cuisinePromptStudioService.createLocalSession(concept, selectedCuisines, useCase);
      newSession.id = response.sessionId;
      newSession.phases = response.phases;
      newSession.dietaryRestrictions = dietaryRestrictions;
      newSession.flavorProfile = flavorProfile;
      newSession.settings.temperature = temperature;
      newSession.settings.includeAuthenticity = includeAuthenticity;
      newSession.settings.includeModern = includeModern;

      cuisinePromptStudioService.saveSession(newSession);
      setCurrentSession(newSession);
      setSessions([...sessions, newSession]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setIsCreatingSession(false);
    }
  };

  const generateFallbackContent = (phase: CuisinePromptPhase, session: CuisinePromptSession): string => {
    // Generate basic content based on the phase type and session data
    const { concept, selectedCuisines, targetUseCase } = session;
    const cuisineText = selectedCuisines.join(' and ');
    
    switch (phase.agentType) {
      case 'abstract-thinker':
        return `Conceptual exploration of ${concept} within the context of ${cuisineText} cuisine, examining the cultural and culinary significance of combining these elements.`;
      
      case 'prompt-improvement':
        return `Enhanced culinary prompt: Create a ${targetUseCase} featuring ${concept} with authentic ${cuisineText} techniques and flavor profiles.`;
      
      case 'image-prompt':
        return `Visual composition: A beautifully plated ${concept} dish showcasing ${cuisineText} culinary traditions, captured in warm natural lighting with vibrant colors and textures.`;
      
      default:
        if (phase.agentType.includes('cuisine')) {
          const cuisine = phase.agentType.replace('cuisine-', '');
          return `${cuisine.charAt(0).toUpperCase() + cuisine.slice(1)} interpretation of ${concept}: Traditional techniques meet modern presentation in this innovative ${targetUseCase}.`;
        }
        return `Culinary creation: ${concept} inspired by ${cuisineText} cuisine for ${targetUseCase}.`;
    }
  };

  const resetForm = () => {
    setConcept('');
    setSelectedCuisines([]);
    setUseCase('recipe');
    setDietaryRestrictions([]);
    setFlavorProfile({
      sweet: 0.5,
      sour: 0.5,
      salty: 0.5,
      bitter: 0.5,
      umami: 0.5,
      spicy: 0.5,
    });
    setTemperature(0.7);
    setIncludeAuthenticity(true);
    setIncludeModern(false);
  };

  const executePhase = async (phase: CuisinePromptPhase) => {
    if (!currentSession) return;

    const updatedSession = { ...currentSession };
    const phaseIndex = updatedSession.phases.findIndex(p => p.id === phase.id);
    if (phaseIndex === -1) return;

    updatedSession.phases[phaseIndex].status = 'in-progress';
    setCurrentSession(updatedSession);

    try {
      const request: CuisinePromptIterateRequest = {
        phaseId: phase.id,
        agentType: phase.agentType,
        feedback: '',  // API requires this field, even if empty
        parameters: {
          temperature: currentSession.settings.temperature,
          includeAuthenticity: currentSession.settings.includeAuthenticity,
          includeModern: currentSession.settings.includeModern,
        },
      };

      const response = await cuisinePromptStudioService.iterate(currentSession.id, request);

      updatedSession.phases = response.updatedPhases;
      
      // Check if we got actual content
      if (response.generatedPrompt) {
        if (!response.generatedPrompt.content || response.generatedPrompt.content.trim() === '') {
          // If content is empty, try to use the result field
          if (response.result && response.result.trim() !== '') {
            response.generatedPrompt.content = response.result;
          } else {
            // Show warning about empty content
            console.warn('Received empty content from agent:', phase.agentType);
            
            // Generate fallback content based on the phase and input
            const fallbackContent = generateFallbackContent(phase, currentSession);
            response.generatedPrompt.content = fallbackContent || `[No content generated by ${phase.agentType}]`;
            
            // Add debug metadata
            response.generatedPrompt.metadata = {
              ...response.generatedPrompt.metadata,
              generatedBy: phase.agentType,
              usedFallback: true,
              emptyResponse: true,
            };
          }
        }
        // Ensure prompt has all required fields
        const promptToAdd = {
          ...response.generatedPrompt,
          createdAt: response.generatedPrompt.createdAt || response.generatedPrompt.timestamp || new Date().toISOString(),
        };
        updatedSession.generatedPrompts.push(promptToAdd);
      }

      // Update phase result if available
      const phaseIndex = updatedSession.phases.findIndex(p => p.id === phase.id);
      if (phaseIndex !== -1 && response.result) {
        updatedSession.phases[phaseIndex].result = response.result;
      }

      cuisinePromptStudioService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
    } catch (err) {
      updatedSession.phases[phaseIndex].status = 'failed';
      updatedSession.phases[phaseIndex].error = err instanceof Error ? err.message : 'Phase execution failed';
      setCurrentSession(updatedSession);
      setError(err instanceof Error ? err.message : 'Phase execution failed');
    }
  };

  const analyzePrompts = async () => {
    if (!currentSession || currentSession.generatedPrompts.length === 0) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      const request: BatchCuisineAnalysisRequest = {
        prompts: currentSession.generatedPrompts.map(p => p.content),
        cuisines: currentSession.selectedCuisines,
        analysisType: 'comprehensive',
      };

      const response = await cuisinePromptStudioService.analyzeBatch(request);

      // Update prompts with analysis results
      const updatedSession = { ...currentSession };
      response.results.forEach((result, index) => {
        if (updatedSession.generatedPrompts[index]) {
          updatedSession.generatedPrompts[index].score = {
            authenticity: result.score,
            creativity: result.score,
            clarity: result.score,
            usefulness: result.score,
          };
        }
      });

      cuisinePromptStudioService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const exportSession = () => {
    if (!currentSession) return;
    
    const exportData = cuisinePromptStudioService.exportSession(currentSession);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cuisine-prompt-${currentSession.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPromptsAsMarkdown = () => {
    if (!currentSession) return;
    
    const markdown = cuisinePromptStudioService.exportPromptsAsMarkdown(currentSession.generatedPrompts);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cuisine-prompts-${currentSession.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPhaseIcon = (agentType: string) => {
    switch (agentType) {
      case 'abstract-thinker':
        return <TipsAndUpdates />;
      case 'prompt-improvement':
        return <Edit />;
      case 'image-prompt':
        return <PhotoCamera />;
      default:
        if (agentType.includes('cuisine')) {
          return <RestaurantMenu />;
        }
        return <Kitchen />;
    }
  };

  const getPhaseStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            flexDirection: { xs: 'row', sm: 'row' }
          }}
        >
          <Kitchen sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Cuisine Prompt Studio
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Generate state-of-the-art culinary prompts using specialized AI agents
        </Typography>
      </Box>


      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: { xs: 1, sm: 2 },
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, v) => setActiveTab(v)}
            sx={{ mb: 2 }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
          >
            <Tab label="New Session" />
            <Tab label="Current Session" disabled={!currentSession} />
            <Tab label="Previous Sessions" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 4 }, maxWidth: 600, mx: 'auto' }}>
              <TextField
                fullWidth
                label="Culinary Concept"
                placeholder="e.g., Fusion tapas combining Japanese and Spanish flavors"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                required
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <FormControl 
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                  '& .MuiSelect-select': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <InputLabel>Select Cuisines</InputLabel>
                <Select
                  multiple
                  value={selectedCuisines}
                  onChange={(e) => setSelectedCuisines(e.target.value as string[])}
                  input={<OutlinedInput label="Select Cuisines" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {AVAILABLE_CUISINES.map((cuisine) => (
                    <MenuItem 
                      key={cuisine} 
                      value={cuisine}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {cuisine}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl 
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                  '& .MuiSelect-select': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <InputLabel>Use Case</InputLabel>
                <Select
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value as CuisinePromptUseCase)}
                  label="Use Case"
                >
                  {Object.entries(USE_CASE_ICONS).map(([key, icon]) => (
                    <MenuItem 
                      key={key} 
                      value={key}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon}
                        <span>{key.charAt(0).toUpperCase() + key.slice(1).replace('-', ' ')}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Dietary Restrictions"
                placeholder="e.g., gluten-free, vegan, nut-free"
                value={dietaryRestrictions.join(', ')}
                onChange={(e) => setDietaryRestrictions(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <Box>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', sm: '1rem' }
                  }}
                >
                  Flavor Profile
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                  {Object.entries(flavorProfile).map(([flavor, value]) => (
                    <Box key={flavor} sx={{ px: { xs: 0.5, sm: 0 } }}>
                      <Typography 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 600, 
                          fontSize: { xs: '0.875rem', sm: '0.95rem' },
                          mb: 2 
                        }}
                      >
                        {flavor.charAt(0).toUpperCase() + flavor.slice(1)}: {Math.round(value * 100)}%
                      </Typography>
                      <Slider
                        value={value}
                        onChange={(_, newValue) => setFlavorProfile({
                          ...flavorProfile,
                          [flavor]: newValue as number,
                        })}
                        min={0}
                        max={1}
                        step={0.1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                        sx={{ mx: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
                  <Typography 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                      mb: 2 
                    }}
                  >
                    Temperature: {temperature}
                  </Typography>
                  <Slider
                    value={temperature}
                    onChange={(_, v) => setTemperature(v as number)}
                    min={0}
                    max={1}
                    step={0.1}
                    valueLabelDisplay="auto"
                    sx={{ mx: 1 }}
                  />
                </Box>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={includeAuthenticity}
                    onChange={(e) => setIncludeAuthenticity(e.target.checked)}
                  />
                }
                label="Include Cultural Authenticity"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={includeModern}
                    onChange={(e) => setIncludeModern(e.target.checked)}
                  />
                }
                label="Include Modern Interpretations"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />

              {error && (
                <Alert 
                  severity="error" 
                  onClose={() => setError(null)}
                  sx={{ 
                    borderRadius: 1.5,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={createSession}
                disabled={isCreatingSession || !concept || selectedCuisines.length === 0}
                startIcon={isCreatingSession ? <CircularProgress size={20} /> : <PlayArrow />}
                sx={{
                  py: 1.5,
                  minHeight: 44,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textTransform: 'none',
                }}
              >
                {isCreatingSession ? 'Creating Session...' : 'Start Cuisine Prompt Generation'}
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {currentSession && (
              <Box>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h5">{currentSession.concept}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentSession.selectedCuisines.join(' × ')} | {currentSession.targetUseCase}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<Analytics />} onClick={analyzePrompts} disabled={isAnalyzing}>
                      Analyze Prompts
                    </Button>
                    <Button startIcon={<Download />} onClick={exportPromptsAsMarkdown}>
                      Export as Markdown
                    </Button>
                    <Button startIcon={<Save />} onClick={exportSession}>
                      Export Session
                    </Button>
                  </Box>
                </Box>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Workflow Phases</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {currentSession.phases.map((phase, index) => (
                        <Fragment key={phase.id}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getPhaseIcon(phase.agentType)}
                                  <span>{phase.name}</span>
                                  <Chip
                                    label={phase.status}
                                    size="small"
                                    color={getPhaseStatusColor(phase.status) as any}
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {phase.description}
                                  </Typography>
                                  {phase.status === 'completed' && phase.result && (
                                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'warning.main' }}>
                                      Result: {phase.result.substring(0, 100)}...
                                    </Typography>
                                  )}
                                  {phase.status === 'failed' && phase.error && (
                                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'error.main' }}>
                                      Error: {phase.error}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Button
                                onClick={() => executePhase(phase)}
                                disabled={phase.status === 'completed' || phase.status === 'in-progress'}
                                startIcon={phase.status === 'in-progress' ? <LinearProgress /> : <PlayArrow />}
                              >
                                Execute
                              </Button>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < currentSession.phases.length - 1 && <Divider />}
                        </Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Generated Prompts ({currentSession.generatedPrompts.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {currentSession.generatedPrompts.length === 0 ? (
                      <Alert severity="info">
                        No prompts generated yet. Execute the workflow phases above to generate prompts.
                      </Alert>
                    ) : (
                      <>
                        {/* Warning if some prompts have empty content */}
                        {currentSession.generatedPrompts.some(p => !p.content || p.content.trim() === '' || p.content.startsWith('[')) && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              Some prompts were generated with fallback content due to API issues. 
                              {currentSession.generatedPrompts.filter(p => p.metadata?.usedFallback).length > 0 && 
                                `(${currentSession.generatedPrompts.filter(p => p.metadata?.usedFallback).length} using fallback)`
                              }
                            </Typography>
                          </Alert>
                        )}
                      <Grid 
                        container 
                        spacing={2}
                        sx={{ 
                          width: '100%',
                          margin: 0,
                          '& > .MuiGrid-item': {
                            paddingLeft: 2
                          }
                        }}
                      >
                        {currentSession.generatedPrompts.map((prompt, idx) => (
                          <Grid item xs={12} md={6} key={prompt.id}>
                            <Card sx={{ 
                              border: prompt.content.startsWith('[No content') ? 2 : 1,
                              borderColor: prompt.content.startsWith('[No content') ? 'warning.main' : 'divider'
                            }}>
                              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Chip
                                  label={prompt.type}
                                  size="small"
                                  color="primary"
                                  icon={USE_CASE_ICONS[prompt.type as keyof typeof USE_CASE_ICONS] || <Kitchen />}
                                />
                                {prompt.score && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Star fontSize="small" color="warning" />
                                    <Typography variant="body2">{Math.round((prompt.score.authenticity + prompt.score.creativity + prompt.score.clarity + prompt.score.usefulness) / 4)}%</Typography>
                                  </Box>
                                )}
                              </Box>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  mb: 2,
                                  fontStyle: prompt.content.startsWith('[No content') ? 'italic' : 'normal',
                                  color: prompt.content.startsWith('[No content') ? 'text.secondary' : 'text.primary'
                                }}
                              >
                                {prompt.content || '[Empty content]'}
                              </Typography>
                              {prompt.metadata.cuisine && (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Cuisine:</strong> {prompt.metadata.cuisine}
                                </Typography>
                              )}
                              {prompt.metadata.dish && (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Dish:</strong> {prompt.metadata.dish}
                                </Typography>
                              )}
                              {prompt.metadata.techniques && prompt.metadata.techniques.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Techniques:</strong> {prompt.metadata.techniques.join(', ')}
                                </Typography>
                              )}
                            </CardContent>
                            <CardActions>
                              <IconButton
                                size="small"
                                onClick={() => navigator.clipboard.writeText(prompt.content)}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </CardActions>
                          </Card>
                        </Grid>
                        ))}
                      </Grid>
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid 
              container 
              spacing={2}
              sx={{ 
                width: '100%',
                margin: 0,
                '& > .MuiGrid-item': {
                  paddingLeft: 2
                }
              }}
            >
              {sessions.map((session) => (
                <Grid item xs={12} md={6} lg={4} key={session.id}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                      >
                        {session.concept}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {session.selectedCuisines.join(' × ')} | {session.targetUseCase}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Created: {new Date(session.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Prompts: {session.generatedPrompts.length}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: { xs: 1, sm: 1.5 } }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setCurrentSession(session);
                          setActiveTab(1);
                        }}
                      >
                        Open
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => {
                          cuisinePromptStudioService.deleteSession(session.id);
                          loadSessions();
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};